'use client';

import { cn } from '@/lib/utils';
import { Star } from 'lucide-react';
import { useState } from 'react';

interface StarRatingProps {
  value: number | null;
  onChange: (value: number) => void;
  max?: number;
  precision?: 0.5 | 1;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function StarRating({
  value,
  onChange,
  max = 5,
  precision = 0.5,
  disabled = false,
  className,
  ariaLabel,
}: StarRatingProps) {
  const [hover, setHover] = useState<number | null>(null);
  const display = hover ?? value ?? 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const current = value ?? 0;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        onChange(Math.min(max, current + precision));
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        onChange(Math.max(0, current - precision));
        break;
      case 'Home':
        e.preventDefault();
        onChange(0);
        break;
      case 'End':
        e.preventDefault();
        onChange(max);
        break;
    }
  };

  return (
    <div
      role='slider'
      aria-label={ariaLabel ?? 'Rating'}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value ?? 0}
      aria-valuetext={`${value ?? 0} out of ${max}`}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      onMouseLeave={() => setHover(null)}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
    >
      {Array.from({ length: max }, (_, i) => {
        const starValue = i + 1;
        const fillPct =
          display >= starValue ? 100 : display >= starValue - 0.5 ? 50 : 0;
        return (
          <div key={starValue} className='relative h-6 w-6'>
            <Star
              className='absolute inset-0 h-6 w-6 text-content-muted'
              strokeWidth={1.5}
            />
            <div
              className='pointer-events-none absolute inset-0 overflow-hidden'
              style={{ width: `${fillPct}%` }}
              aria-hidden='true'
            >
              <Star
                className='h-6 w-6 fill-status-warning text-status-warning'
                strokeWidth={1.5}
              />
            </div>
            {precision === 0.5 && (
              <span
                aria-hidden='true'
                className={cn(
                  'absolute left-0 top-0 z-10 h-full w-1/2',
                  !disabled && 'cursor-pointer'
                )}
                onClick={disabled ? undefined : () => onChange(starValue - 0.5)}
                onMouseEnter={() => setHover(starValue - 0.5)}
              />
            )}
            <span
              aria-hidden='true'
              className={cn(
                'absolute top-0 z-10 h-full',
                !disabled && 'cursor-pointer',
                precision === 0.5 ? 'left-1/2 w-1/2' : 'left-0 w-full'
              )}
              onClick={disabled ? undefined : () => onChange(starValue)}
              onMouseEnter={() => setHover(starValue)}
            />
          </div>
        );
      })}
    </div>
  );
}
